/**
 * Nhắc bản sổ tay chưa xác nhận. Mounted app-level beside ScheduleRegisterForcedPopup.
 *
 * Từng là nhắc "mềm" (có Để sau, bấm nền để đóng). Giờ sổ tay là bước bắt buộc — cổng chặn
 * employee-handbook-gate đưa người còn nợ về /handbook — nên popup chỉ còn nút đọc.
 *
 * Partner accounts never see it — /current 403s for anyone outside the employee directory, and
 * a failed query renders nothing.
 */
import { useState } from 'react'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { authClient } from '../lib/auth-client'
import { useAccess } from '../lib/access-context'
import { useT } from '../i18n/language-provider'
import { Button } from '../components/ui/button'
import { getHandbookAckState } from '../lib/api-client-employee-handbook'

/**
 * Whether the ack-state query may fire.
 *
 * The handbook is employee-only, and `/ack-state` 403s for anyone outside the employee directory.
 * The query key is shared with the gate, but `enabled` is per-observer: if this popup does not also
 * gate on `isEmployee`, the request fires for partner accounts and the browser logs a 403 on every
 * navigation. Pure, so the guard itself is tested rather than the component around it.
 */
export function shouldAskAckState(input: {
  hasSession: boolean
  sessionPending: boolean
  isEmployee: boolean
}): boolean {
  return input.hasSession && !input.sessionPending && input.isEmployee
}

export function EmployeeHandbookAckPopup() {
  const t = useT()
  const navigate = useNavigate()
  const access = useAccess()
  const { data: session, isPending } = authClient.useSession()
  const [dismissed, setDismissed] = useState(false)
  const pathname = useRouterState({ select: (s) => s.location.pathname })

  const { data } = useQuery({
    // Cùng khoá với cổng chặn (employee-handbook-gate), để một lần xác nhận làm mới cả hai.
    queryKey: ['handbook', 'ack-state'],
    queryFn: getHandbookAckState,
    enabled: shouldAskAckState({
      hasSession: !!session,
      sessionPending: isPending,
      isEmployee: access.isEmployee,
    }),
    // The handbook changes a few times a year; asking again mid-session is pointless.
    staleTime: 60 * 60 * 1000,
    retry: false,
  })

  if (dismissed) return null
  const current = data?.success ? data.data : null
  if (!current?.mustAck) return null
  // Cổng chặn (employee-handbook-gate) đã đưa người còn nợ về /handbook; nhắc chồng lên chính
  // trang đó chỉ là một cú bấm thừa trước khi đọc.
  if (pathname === '/handbook' || pathname === '/handbook/start') return null

  return (
    // Không có "Để sau", không bấm nền để đóng: sổ tay là bước bắt buộc (xem
    // employee-handbook-gate), popup chỉ còn một lối là đọc.
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        id="handbook-ack-popup"
        role="status"
        className="flex w-full max-w-lg flex-col gap-3 rounded-lg border border-border bg-surface p-4 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="text-sm font-semibold text-fg">{t('handbook.popup.title')}</p>
        <p className="text-sm text-fg-soft">
          {(current.neverAcknowledged ? t('handbook.popup.bodyFirst') : t('handbook.popup.body'))
            .replace('{v}', String(current.ackVersion ?? current.version))}
        </p>
        <div className="flex justify-end gap-2">
          {/* Người popup nhắc là người CHƯA đọc xong — đường của họ là lối từng bước. Trang mục
              lục /handbook dành cho người đã xong vào tra cứu (user 260819). */}
          <Button
            onClick={() => {
              setDismissed(true)
              void navigate({ to: '/handbook/start' })
            }}
          >
            {current.neverAcknowledged ? t('handbook.popup.readFirst') : t('handbook.popup.read')}
          </Button>
        </div>
      </div>
    </div>
  )
}
